import React from "react";
import {IconButton, Link, Typography} from "@mui/material";
import {AutoAwesomeOutlined, AutorenewOutlined, DesignServicesOutlined, SchoolOutlined, VisibilityOutlined, AccountTreeOutlined, HowToRegOutlined, ChangeCircleOutlined,
        StraightenOutlined, Inventory2Outlined, ReceiptLongOutlined, AdminPanelSettingsOutlined, FlagOutlined, TimelineOutlined, AttachFileOutlined,
    ExploreOutlined, DescriptionOutlined, LockOutlined, FactCheckOutlined, HistoryOutlined, WorkspacePremiumOutlined, MenuBookOutlined, SortByAlphaOutlined} from "@mui/icons-material";
import DocSearch from "../components/Common/DocSearch";
import {DOCUMENTATION_PDF} from "../data/documentationPdf";
import PageHeader from "../components/Common/PageHeader";
import useBreakpoint from "../util/useBreakpoint";
import {Black, Blue3, Grey2, Grey5, White} from "../theme/styles";
import css_buttons from "../components/Common/css/CCButton.module.scss";

/**
 * The index of the user guides, at "/documentation".
 *
 * The guides are static HTML under public/docs and are NOT React pages: they are written and read as
 * documents, they outlive the components they describe, and they have their own navigation. So a card
 * opens one in a named popup window rather than routing to it -- the same call the Visibility tab
 * already makes from its "Visibility guide" button, and the same window name, so a guide opened from
 * either place reuses the one window instead of stacking up duplicates.
 *
 * One card per file in public/docs. The title and description below are the guide's own <h1> and its
 * header line, copied verbatim, so this page cannot describe a guide as something it is not.
 */

type Guide = {
    icon: React.ReactNode;
    title: string;
    description: string;
    /** Path under public/, e.g. "/docs/visibility.html". */
    href: string;
    /** window.open target name -- stable per guide so reopening reuses that guide's window. */
    windowName: string;
};

export default function Documentation() {
    const breakpoint = useBreakpoint();

    const iconSx = {color: Blue3, width: "75px", height: "75px"};

    // THE SPINE, IN READING ORDER AND NOT SORTED. The guides below have no natural order, which is
    // why that list is alphabetical. These six do: each is a problem the previous one leaves
    // behind, and each ends by naming the next. Sorting them would destroy the only thing they
    // have that a pile of guides does not.
    const spine: Guide[] = [
        {icon: <ExploreOutlined sx={iconSx}/>, title: "Start here",
         href: "/docs/start-here.html", windowName: "cresStartHere",
         description: "What this product is, the one question it exists to answer, and how that question breaks into six smaller ones. Twenty minutes, nothing else assumed."},
        {icon: <DescriptionOutlined sx={iconSx}/>, title: "1 · Getting a record under control",
         href: "/docs/problem-under-control.html", windowName: "cresProblem1",
         description: "Nothing can be approved, restricted, revised or trained on until it is a record rather than a file. Shapes, forms, revisions and the states."},
        {icon: <LockOutlined sx={iconSx}/>, title: "2 · Who can see it, who owns it",
         href: "/docs/problem-who-sees-it.html", windowName: "cresProblem2",
         description: "A record everybody can change is not controlled; one nobody can find is not useful. Ownership, the four routes to a record, and why reads are opt-in."},
        {icon: <FactCheckOutlined sx={iconSx}/>, title: "3 · Approved, provably",
         href: "/docs/problem-approval.html", windowName: "cresProblem3",
         description: "Not whether it was approved, but by whom, with what authority, meaning what — and that what they signed is what is in force."},
        {icon: <HistoryOutlined sx={iconSx}/>, title: "4 · Changing it without losing the past",
         href: "/docs/problem-changing-it.html", windowName: "cresProblem4",
         description: "Documents change; what the previous version said must not. Revisions, change orders, and the three different ways a record can end."},
        {icon: <WorkspacePremiumOutlined sx={iconSx}/>, title: "5 · Proving people were competent",
         href: "/docs/problem-competence.html", windowName: "cresProblem5",
         description: "An approved procedure proves nothing if the people working to it were never trained on that revision. The problem most teams meet during the audit."},
        {icon: <FlagOutlined sx={iconSx}/>, title: "6 · Running the work to a plan",
         href: "/docs/problem-to-a-plan.html", windowName: "cresProblem6",
         description: "Design controls: phases approved before the next one opened, rather than a plan written afterwards to match what happened."},
    ];

    const reference: Guide[] = [
        {icon: <MenuBookOutlined sx={iconSx}/>, title: "Glossary",
         href: "/docs/glossary.html", windowName: "cresGlossary",
         description: "Every term this documentation uses, explained for somebody meeting it for the first time — what it is, why it exists, and where it matters."},
        {icon: <SortByAlphaOutlined sx={iconSx}/>, title: "Index",
         href: "/docs/subject-index.html", windowName: "cresIndex",
         description: "Every indexed term with the sections that discuss it, bold where it is defined. Generated from the guides, so it cannot go stale."},
    ];

    const guides: Guide[] = [
        {
            icon: <AccountTreeOutlined sx={iconSx}/>,
            title: "Bill of materials",
            description: "What an item is made of: the lines and their units, configurations, the explosion down and the where-used up, the roll-up in base units, and how a released bill stays under change control.",
            href: "/docs/bill-of-materials.html",
            windowName: "cresBillOfMaterialsDocs",
        },
        {
            icon: <StraightenOutlined sx={iconSx}/>,
            title: "Units of measure",
            description: "The list every quantity states its unit from, the dimension each unit measures, the base unit per dimension and the factors that let quantities in different units be added.",
            href: "/docs/units-of-measure.html",
            windowName: "cresUnitsOfMeasureDocs",
        },
        {
            icon: <Inventory2Outlined sx={iconSx}/>,
            title: "Inventory and lots",
            description: "A lot is one quantity of one item with a history of its own; an inventory is a collection of lots of one item. Transfers, cycle counts, the quantity on hand, and what you see of lots you may not open.",
            href: "/docs/inventory-and-lots.html",
            windowName: "cresInventoryLotsDocs",
        },
        {
            icon: <ReceiptLongOutlined sx={iconSx}/>,
            title: "Purchase orders and receipts",
            description: "Ordering from a supplier, approval by amount, receiving against the order line by line, turning what arrived into lots, and who supplies an item in the first place.",
            href: "/docs/purchase-orders-and-receipts.html",
            windowName: "cresPurchaseOrdersDocs",
        },
        {
            icon: <TimelineOutlined sx={iconSx}/>,
            title: "Project plans",
            description: "A plan is phases; a phase is milestones; a phase is approved before the next one is relied on. How to build one, how to run it, and what the approval on a phase actually means.",
            href: "/docs/project-plans.html",
            windowName: "cresProjectPlanDocs",
        },
        {
            icon: <HowToRegOutlined sx={iconSx}/>,
            title: "Approvals and electronic signatures",
            description: "How a record gets reviewed and signed: setting up who has to approve what, sending a revision for approval, signing or rejecting it, and what happens to the record at the end.",
            href: "/docs/approvals.html",
            windowName: "cresApprovalsDocs",
        },
        {
            icon: <ChangeCircleOutlined sx={iconSx}/>,
            title: "Change orders",
            description: "How to package several records into one controlled change: raising it, putting records in it, getting it approved, and what closing it does to everything inside.",
            href: "/docs/change-orders.html",
            windowName: "cresChangeOrderDocs",
        },
        {
            icon: <DesignServicesOutlined sx={iconSx}/>,
            title: "Designing a form",
            description: "How to build the questions people actually see — which of a shape's fields are asked, in what order, and under which headings.",
            href: "/docs/form-designer.html",
            windowName: "cresFormDesignerDocs",
        },
        {
            icon: <AutorenewOutlined sx={iconSx}/>,
            title: "Record lifecycle",
            description: "What the states on a record mean, how it moves between them, and which moves you cannot take back.",
            href: "/docs/lifecycle.html",
            windowName: "cresLifecycleDocs",
        },
        {
            icon: <SchoolOutlined sx={iconSx}/>,
            title: "Training",
            description: "How a record comes to require training, who is asked to take it, what completing it records, and when it comes round again.",
            href: "/docs/training.html",
            windowName: "cresTrainingDocs",
        },
        {
            icon: <VisibilityOutlined sx={iconSx}/>,
            title: "Visibility",
            description: "Who owns a record, who else can reach it, what they may do with it, and how far your own view reaches.",
            href: "/docs/visibility.html",
            windowName: "cresVisibilityDocs",
        },
        {
            icon: <AdminPanelSettingsOutlined sx={iconSx}/>,
            title: "Roles and capabilities",
            description: "What a privilege, a capability and a role are, the levels each quality record type offers, what a new account starts with, and how to set up roles so that people can do their work and no more.",
            href: "/docs/roles-and-capabilities.html",
            windowName: "cresRolesCapabilitiesDocs",
        },
        {
            icon: <AttachFileOutlined sx={iconSx}/>,
            title: "Attachments",
            description: "How files are stored, added, previewed and downloaded; what each file's audit trail records; what release does to a record's files, the controlled copy and the stamps; and the regulations and standards this is built to support.",
            href: "/docs/attachments.html",
            windowName: "cresAttachmentsDocs",
        },
        {
            icon: <AutoAwesomeOutlined sx={iconSx}/>,
            title: "Asking about attachments",
            description: "Put a question to the assistant about the files on your records \u2014 one file, one record, or a set you pick out of a list \u2014 and get an answer that cites the page it came from.",
            href: "/docs/asking-about-attachments.html",
            windowName: "cresAttachmentQuestionsDocs",
        },
    ];

    const openGuide = (guide: Guide) => {
        window.open(guide.href, guide.windowName, "width=1200,height=900,scrollbars=yes,resizable=yes");
    };

    function renderCard(guide: Guide, cardWidth: string) {
        return (
            <div key={guide.href} style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", margin: "0 10px 10px 0", border: `1px solid ${Grey2}`, borderRadius: "10px", width: cardWidth, padding: "15px", boxSizing: "border-box"}}>
                <div style={{display: "flex", flexDirection: "row"}}>
                    <IconButton className={css_buttons.blueIconButton} style={{height: "75px"}} onClick={() => openGuide(guide)}>
                        {guide.icon}
                    </IconButton>
                    <div style={{marginLeft: "15px", marginTop: "25px"}}>
                        <Link underline="none" variant="inherit" color="inherit" href="#" onClick={(e) => {
                            e.preventDefault();
                            openGuide(guide);
                        }}>
                            <Typography style={{fontSize: "16px", fontWeight: 400, color: Black, textAlign: "left"}}>{guide.title}</Typography>
                        </Link>
                    </div>
                </div>
                <div style={{marginTop: "10px", height: "60px"}}>
                    <Typography style={{fontSize: "14px", fontWeight: 400, color: Grey5, textAlign: "left"}}>{guide.description}</Typography>
                </div>
                <div style={{marginTop: "20px", borderBottom: `1px solid ${Grey2}`}}/>
                <div style={{marginTop: "10px"}}>
                    <Link underline="none" variant="inherit" color="inherit" href="#" onClick={(e) => {
                        e.preventDefault();
                        openGuide(guide);
                    }}>
                        <Typography style={{fontSize: "13px", fontWeight: 600, color: Blue3, marginTop: "10px"}}>Open guide</Typography>
                    </Link>
                </div>
            </div>
        );
    }

    function renderGuides() {
        const bp = breakpoint.getBreakPointName();
        const cardsPerRow = bp === "xl" ? 4
            : bp === "lg" ? 3
            : bp === "md" ? 2
            : bp === "sm" ? 2
            : 1;
        const cardWidth = `calc(${100 / cardsPerRow}% - 10px)`;

        // Alphabetical by title: the guides have no natural reading order, and a fixed one spares a
        // reader from re-scanning the page every time a guide is added.
        const sortedGuides = [...guides].sort((a, b) => a.title.localeCompare(b.title));
        const rows: Guide[][] = [];
        for (let i = 0; i < sortedGuides.length; i += cardsPerRow) {
            rows.push(sortedGuides.slice(i, i + cardsPerRow));
        }

        const asRows = (list: Guide[]) => {
            const out: Guide[][] = [];
            for (let i = 0; i < list.length; i += cardsPerRow) out.push(list.slice(i, i + cardsPerRow));
            return out;
        };

        const section = (heading: string, blurb: string, list: Guide[]) => (
            <div key={heading} style={{marginTop: "22px"}}>
                <Typography style={{fontSize: 16, fontWeight: 600, color: Black}}>{heading}</Typography>
                <Typography style={{fontSize: 13, color: Grey5, margin: "2px 0 4px"}}>{blurb}</Typography>
                {asRows(list).map((row, rIdx) => (
                    <div key={rIdx} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: rIdx === 0 ? "16px" : "0"}}>
                        {row.map(guide => renderCard(guide, cardWidth))}
                    </div>
                ))}
            </div>
        );

        return (
            <div style={{padding: "10px 10px 10px 10px"}}>
                <Typography style={{fontSize: 13, color: Grey5, margin: "14px 0 4px"}}>
                    Every guide this app ships. Each opens in its own window, so you can read it beside what you are doing — and print it.{" "}
                    <Link underline="none" href={DOCUMENTATION_PDF} target="_blank" rel="noreferrer"
                          style={{color: Blue3, fontWeight: 600}}>
                        All of it as one PDF
                    </Link>, with a table of contents.
                </Typography>

                <DocSearch open={(file, hash) => {
                    const all = [...spine, ...guides, ...reference];
                    const named = all.find(one => one.href === "/docs/" + file);
                    window.open("/docs/" + file + (hash ? "#" + hash : ""),
                                named?.windowName ?? "cresDocs",
                                "width=1200,height=900,scrollbars=yes,resizable=yes");
                }}/>

                {section("New here? Read these, in order",
                         "What the product is, then the one problem it solves, broken into six — largest first. Each chapter carries its own step-by-step.",
                         spine)}
                {section("Doing the work",
                         "Each subject in full, for somebody in the middle of doing it.",
                         sortedGuides)}
                {section("Reference",
                         "For looking something up rather than reading through.",
                         reference)}
            </div>
        );
    }

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100%", backgroundColor: White}}>
            <PageHeader title="Documentation"/>
            {renderGuides()}
        </div>
    );
}
